import { useVirtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import { ChevronDownIcon, XIcon } from 'lucide-react';
import React from 'react';
import Select, {
  components,
  createFilter,
  type ClearIndicatorProps,
  type DropdownIndicatorProps,
  type GroupBase,
  type InputProps,
  type LoadingIndicatorProps,
  type MenuListProps,
  type MultiValueRemoveProps,
  type OptionProps,
  type Props as SelectProps,
} from 'react-select';
import CreatableSelect, { type CreatableProps } from 'react-select/creatable';
import { ScrollArea, ScrollAreaViewport } from './scroll-area';

function DropdownIndicator<Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  props: DropdownIndicatorProps<Option, IsMulti, Group>,
) {
  return (
    <components.DropdownIndicator {...props}>
      <ChevronDownIcon className='size-4' />
    </components.DropdownIndicator>
  );
}

function Input<Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  props: InputProps<Option, IsMulti, Group>,
) {
  return <components.Input className='react-select__input' {...props} />;
}

function ClearIndicator<Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  props: ClearIndicatorProps<Option, IsMulti, Group>,
) {
  return (
    <components.ClearIndicator {...props}>
      <XIcon className='size-4' />
    </components.ClearIndicator>
  );
}

function LoadingIndicator<Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  _props: LoadingIndicatorProps<Option, IsMulti, Group>,
) {
  return (
    <div className='flex items-center pr-2'>
      <span className='inline-block size-4 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent' />
    </div>
  );
}

function OptionComponent<Option, IsMulti extends boolean, Group extends GroupBase<Option>>(
  props: OptionProps<Option, IsMulti, Group>,
) {
  const { children, innerProps } = props;
  // Remove onMouseMove/onMouseOver to reduce re-renders & heavy hover processing
  const { onMouseMove, onMouseOver, ...safeInnerProps } = innerProps;
  return (
    <components.Option
      {...props}
      innerProps={safeInnerProps}
      className={clsx('flex items-center', (props as { className?: string }).className)}
    >
      {children}
    </components.Option>
  );
}

const MultiValueRemove = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: MultiValueRemoveProps<Option, IsMulti, Group>,
) => (
  <components.MultiValueRemove {...props}>
    <XIcon className='size-3' />
  </components.MultiValueRemove>
);

type MenuListConfig = {
  menuListMaxHeight?: number;
  enableVirtualization?: boolean;
  virtualOptionHeight?: number | ((index: number) => number);
  useDynamicOptionMeasurements?: boolean;
  overscan?: number;
};

export type BaseVirtualizationProps = {
  menuListMaxHeight?: number;
  enableVirtualization?: boolean;
  virtualOptionHeight?: number | ((index: number) => number);
  useDynamicOptionMeasurements?: boolean;
  overscan?: number;
};

type CreatableAugmentedProps<
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
> = Omit<CreatableProps<Option, IsMulti, Group>, never> &
  BaseVirtualizationProps & { createAble: true };

type RegularAugmentedProps<Option, IsMulti extends boolean, Group extends GroupBase<Option>> = Omit<
  SelectProps<Option, IsMulti, Group>,
  never
> &
  BaseVirtualizationProps & { createAble?: false };

export type SelectComponentProps<
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
> = CreatableAugmentedProps<Option, IsMulti, Group> | RegularAugmentedProps<Option, IsMulti, Group>;

function makeMenuList(config: MenuListConfig) {
  return <
    Option,
    IsMulti extends boolean = false,
    Group extends GroupBase<Option> = GroupBase<Option>,
  >(
    props: MenuListProps<Option, IsMulti, Group>,
  ) => {
    const { children, options, innerProps } = props;
    const maxHeight: number = config.menuListMaxHeight ?? 240;
    const enableVirtualization = Boolean(config.enableVirtualization);
    const estimateSize = (index: number) => {
      if (typeof config.virtualOptionHeight === 'function') {
        return config.virtualOptionHeight(index);
      }

      if (typeof config.virtualOptionHeight === 'number') {
        return config.virtualOptionHeight;
      }

      return 32; // default fallback
    };

    const hasGroups: boolean =
      Array.isArray(options) && options.some(os => (os as { options?: unknown[] })?.options);

    const flatChildren = React.Children.toArray(children);
    const parentRef = React.useRef<HTMLDivElement | undefined>(undefined);
    const virtualizer = useVirtualizer({
      count: enableVirtualization && !hasGroups ? flatChildren.length : 0,
      getScrollElement: () => parentRef.current || null,
      estimateSize: i => estimateSize(i),
      overscan: config.overscan ?? 6,
      // Dynamic measurement handled via explicit ref callback below
    });

    // Only constrain max-height; allow natural height until options overflow.
    // For virtualized lists we keep a fixed height to enable scroll calculations.
    const heightClassMap: Record<number, string> = {
      240: 'max-h-[240px]',
      300: 'max-h-[300px]',
      400: 'max-h-[400px]',
    };
    const heightClasses = heightClassMap[maxHeight];
    // Inline maxHeight style only when not mapped to Tailwind utility.
    const hasMappedHeightClass = Boolean(heightClasses);
    const isVirtual = enableVirtualization && !hasGroups;

    return (
      <ScrollArea
        className={clsx(
          'w-full',
          heightClasses,
          (innerProps as Record<string, unknown>)?.className as string | undefined,
        )}
        style={(() => {
          // For non-virtual lists we need a fixed height when content exceeds maxHeight
          // so that the Radix ScrollArea can render a scrollbar.
          const flatCount = flatChildren.length;
          const approximateHeight = flatCount * estimateSize(0);
          const shouldClampHeight = !isVirtual && approximateHeight > maxHeight;
          const style: React.CSSProperties = {};
          if (!hasMappedHeightClass) {
            style.maxHeight = maxHeight;
          }

          if (shouldClampHeight) {
            style.height = maxHeight; // force viewport to clamp & enable scrolling
          }

          return style;
        })()}
      >
        <ScrollAreaViewport
          {...innerProps}
          ref={el => {
            parentRef.current = el || undefined;
          }}
          style={{ height: maxHeight, maxHeight }}
          className={clsx('w-full relative')}
        >
          {isVirtual ? (
            <div
              className='w-full relative'
              style={{ height: virtualizer.getTotalSize(), position: 'relative' }}
            >
              {virtualizer.getVirtualItems().map(vi => {
                const child = flatChildren[vi.index];
                return (
                  <div
                    key={vi.key}
                    ref={el => {
                      if (el) {
                        virtualizer.measureElement(el);
                      }
                    }}
                    className='absolute top-0 left-0 w-full'
                    style={{
                      transform: `translateY(${vi.start}px)`,
                      height: `${vi.size}px`,
                    }}
                  >
                    {child}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className='w-full'>{children}</div>
          )}
        </ScrollAreaViewport>
      </ScrollArea>
    );
  };
}

const controlStyles = {
  base: 'react-base-select flex w-full !min-h-[36px] rounded-md border border-input bg-transparent px-2 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground',
  focus: 'outline-none ring-1 ring-ring',
  nonFocus: 'border-border',
};
const placeholderStyles = 'text-muted-foreground text-sm ml-1';
const selectInputStyles = 'text-foreground text-sm ml-1';
const valueContainerStyles = 'text-foreground text-sm';
const singleValueStyles = 'ml-1';
const multiValueStyles =
  'ml-1 bg-background border border-border rounded items-center px-1 my-0.5 gap-1.5';
const multiValueLabelStyles = 'leading-5 text-xs py-0.5';
const multiValueRemoveStyles =
  'bg-white hover:bg-red-50 hover:text-red-800 text-gray-500 hover:border-red-300 rounded-md bg-background';
const indicatorsContainerStyles = 'gap-1 bg-background rounded-lg';
const clearIndicatorStyles = 'text-gray-500 rounded-md hover:text-red-800';
const indicatorSeparatorStyles = 'bg-muted';
const dropdownIndicatorStyles = 'hover:text-foreground text-gray-500';
const menuStyles = 'mt-2 p-0 border border-border bg-background text-sm rounded-lg !z-50';
const optionsStyle =
  'bg-background p-2 border-0 text-sm flex items-center hover:bg-secondary hover:cursor-pointer';
const groupHeadingStyles = 'ml-3 mt-2 mb-1 text-gray-500 text-sm bg-background';
const noOptionsMessageStyles = 'text-muted-foreground bg-background';

function SelectComponentInner<
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: SelectComponentProps<Option, IsMulti, Group>,
  ref: React.ForwardedRef<CreatableSelect | Select>,
): React.JSX.Element {
  const {
    createAble,
    options,
    value,
    onChange,
    noOptionsMessage,
    isMulti,
    isDisabled,
    components: passedComponents,
    isLoading,
    placeholder,
    defaultValue,
    menuListMaxHeight,
    enableVirtualization,
    virtualOptionHeight,
    useDynamicOptionMeasurements,
    overscan,
    ...rest
  } = props as SelectComponentProps<Option, IsMulti, Group> & { createAble: boolean };

  const creatableProps = createAble
    ? (props as CreatableAugmentedProps<Option, IsMulti, Group>)
    : undefined;

  const Comp = createAble ? CreatableSelect : Select;

  // Auto virtualization: if caller did not set enableVirtualization explicitly, turn it on when
  // there are more than the threshold number of flat options (ignoring groups for simplicity).
  const AUTO_VIRTUALIZE_THRESHOLD = 50;
  const isGroup = (obj: unknown): obj is GroupBase<Option> =>
    typeof obj === 'object' &&
    obj !== null &&
    'options' in (obj as Record<string, unknown>) &&
    Array.isArray((obj as { options: unknown }).options);

  const optionCount = Array.isArray(options)
    ? (options as Array<Option | GroupBase<Option>>).reduce<number>((acc, o) => {
        if (isGroup(o)) {
          return acc + o.options.length;
        }

        return acc + 1;
      }, 0)
    : 0;
  const resolvedEnableVirtualization =
    typeof enableVirtualization === 'boolean'
      ? enableVirtualization
      : optionCount > AUTO_VIRTUALIZE_THRESHOLD;

  return (
    <Comp
      ref={ref as never}
      unstyled
      isClearable
      isSearchable
      value={value}
      isDisabled={isDisabled}
      onCreateOption={creatableProps?.onCreateOption}
      isMulti={isMulti}
      isLoading={isLoading}
      placeholder={placeholder}
      filterOption={createFilter({ ignoreAccents: false })}
      components={{
        DropdownIndicator,
        ClearIndicator,
        LoadingIndicator,
        MultiValueRemove,
        Option: OptionComponent,
        Input,
        MenuList: makeMenuList({
          menuListMaxHeight,
          enableVirtualization: resolvedEnableVirtualization,
          virtualOptionHeight,
          useDynamicOptionMeasurements,
          overscan,
        }),
        ...passedComponents,
      }}
      defaultValue={value}
      options={options}
      noOptionsMessage={noOptionsMessage}
      onChange={onChange}
      classNames={{
        control: ({ isFocused, isDisabled }) =>
          clsx(
            isDisabled ? 'cursor-not-allowed opacity-50' : '',
            isFocused ? controlStyles.focus : controlStyles.nonFocus,
            controlStyles.base,
          ),
        placeholder: () => placeholderStyles,
        input: () => selectInputStyles,
        option: () => optionsStyle,
        menu: () => menuStyles,
        valueContainer: () => valueContainerStyles,
        singleValue: () => singleValueStyles,
        multiValue: () => multiValueStyles,
        multiValueLabel: () => multiValueLabelStyles,
        multiValueRemove: () => multiValueRemoveStyles,
        indicatorsContainer: () => indicatorsContainerStyles,
        clearIndicator: () => clearIndicatorStyles,
        indicatorSeparator: () => indicatorSeparatorStyles,
        dropdownIndicator: () => dropdownIndicatorStyles,
        groupHeading: () => groupHeadingStyles,
        noOptionsMessage: () => noOptionsMessageStyles,
      }}
      {...rest}
    />
  );
}

export const SelectComponent = React.forwardRef(SelectComponentInner) as <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: SelectComponentProps<Option, IsMulti, Group>,
) => React.JSX.Element;
